import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import { Colors } from '../../themes';
import styled from 'styled-components';
import { formatBytes } from '../../lib/Utils';
import i18n from '../../i18n';

const getColor = (props) => {
  if (props.isDragAccept) {
    return Colors.blue;
  }
  if (props.isDragReject) {
    return '#ff1744';
  }
  if (props.isFocused) {
    return Colors.blue;
  }
  return '#cccccc';
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const useStyles = makeStyles((theme) => {
  return {
    dropzoneContainer: {
      width: 500,
      padding: 20,
      background: Colors.dropzoneGrey,
    },
    topContainer: {},
    dragInstructions: {
      fontSize: 16,
      color: Colors.black,
      textAlign: 'center',
    },
    selectFileContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
    },
    bottomContainer: {
      marginTop: 20,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingLeft: 4,
      marginRight: -8,
    },
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.black,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        color: Colors.white,
        background: Colors.disabledGrey,
      },
      '&:disabled': {
        border: `1px solid ${Colors.disabledGrey}`,
        cursor: 'not-allowed',
        pointerEvents: 'all !important',
        '&:hover': {
          color: Colors.disabledGrey,
        },
      },
    },
    saveButton: {
      color: Colors.white,
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 12,
      paddingRight: 12,
    },
  };
});

const Dropzone = ({ isFileValid, setFileValid, isUploading, uploadFile }) => {
  const classes = useStyles();
  const onDrop = useCallback((acceptedFiles) => {}, []);
  const { acceptedFiles, getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    maxFiles: 1,
    disabled: isUploading,
  });

  useEffect(() => {
    const extension = acceptedFiles?.[0]?.name?.slice(acceptedFiles?.[0].name?.length - 3);
    if (acceptedFiles?.length > 0) {
      setFileValid(extension === 'duf');
    }
  }, [acceptedFiles]);

  return (
    <div>
      <Container
        {...getRootProps({ isFocused, isDragAccept, isDragReject })}
        className={classes.dropzoneContainer}
        disabled={isUploading}
      >
        <input {...getInputProps()} />
        <div className={classes.topContainer}>
          {acceptedFiles?.length === 0 ? (
            <p className={classes.dragInstructions}>{i18n.t('alert.dropzone.initialInstructions')}</p>
          ) : !isFileValid ? (
            <p className={classes.dragInstructions}>{i18n.t('alert.dropzone.invalidFileText')}</p>
          ) : isUploading ? (
            <p className={classes.dragInstructions}>{i18n.t('alert.dropzone.uploadingText')}</p>
          ) : (
            <p className={classes.dragInstructions}>{i18n.t('alert.dropzone.validFileText')}</p>
          )}
          <p className={classes.dragInstructions}></p>
        </div>
        <div className={classes.selectFileContainer}>
          <Button
            type="button"
            variant="outlined"
            className={clsx([classes.button, classes.smallSizeButton])}
            disabled={isUploading}
          >
            select file
          </Button>
        </div>
      </Container>
      <div className={classes.bottomContainer}>
        <p>{acceptedFiles.length > 0 ? `${acceptedFiles[0]?.name} (${formatBytes(acceptedFiles[0].size)})` : ''}</p>
        <Button
          type="button"
          variant="contained"
          className={clsx([classes.button, classes.smallSizeButton, classes.saveButton])}
          disabled={!acceptedFiles[0] || !isFileValid}
          onClick={() => uploadFile(acceptedFiles[0])}
        >
          Upload
        </Button>
      </div>
    </div>
  );
};

export default Dropzone;
